import {
	getSelectedGroupMovedRects,
} from '../layout/webDashboardSelectionGeometry';
import {
	normalizeRect,
	rectsIntersect,
	sameIdList,
	sameOffset,
	sameRect,
} from '../layout/webDashboardGeometry';

export const createSelectionHandlers = ({
	mode,
	multiSelectEnabled,
	selectedWidgetIds,
	selectedWidgetIdSet,
	dashboard,
	getEditableMetrics,
	getWidgetRectPx,
	getSelectedGroupMoveContext,
	clampSelectedGroupDelta,
	selectedGroupDragRef,
	selectedGroupDragOffsetRef,
	selectionDragRef,
	suppressSelectionClickRef,
	setSelectedGroupDragOffset,
	setSelectionRect,
	setSelectedWidgetIds,
	constrainRectToBackgroundImage,
	checkOverlap,
	setAlertMessage,
	t,
	setAlertType,
	setShowAlert,
	storeWidgetPosition,
	setDashboard,
	sortWidgets,
	findNearestEmptySpotForWidgets,
	cloneDashboardValue,
	isWidgetFreeMovementEnabled,
}) => {
	const commitSelectedGroupMove = (deltaX, deltaY) => {
		const context = getSelectedGroupMoveContext();
		if (!context) return false;

		const clampedDelta = clampSelectedGroupDelta(deltaX, deltaY, context);
		const { hasConflict, movedRectsById } = getSelectedGroupMovedRects({
			selectedRects: context.selectedRects,
			selectedIdSet: context.selectedIdSet,
			currentWidgets: dashboard?.widgets || [],
			deltaX: clampedDelta.x,
			deltaY: clampedDelta.y,
			padLeft: context.padLeft,
			padRight: context.padRight,
			padTop: context.padTop,
			padBottom: context.padBottom,
			effectiveWidth: context.effectiveWidth,
			effectiveHeight: context.effectiveHeight,
			constrainRectToBackgroundImage,
			checkOverlap,
		});

		if (hasConflict) {
			setAlertMessage(t.noSpaceForDuplication || 'No valid space available for the selected widgets.');
			setAlertType('warning');
			setShowAlert(true);
			return false;
		}

		const updatedWidgets = (dashboard?.widgets || []).map((widget) => {
			const movedRect = movedRectsById.get(String(widget.id));
			return movedRect ? storeWidgetPosition(widget, movedRect, context.metrics) : widget;
		});

		setDashboard({
			...dashboard,
			widgets: sortWidgets(updatedWidgets),
		});
		return true;
	};

	const duplicateSelectedGroup = () => {
		if (mode !== 'edit' || !multiSelectEnabled || selectedWidgetIds.length === 0) {
			return false;
		}

		const context = getSelectedGroupMoveContext();
		if (!context) return false;

		const currentWidgets = dashboard?.widgets || [];
		const unselectedWidgets = currentWidgets.filter((widget) => !context.selectedIdSet.has(String(widget.id)));
		const { found, left, top } = findNearestEmptySpotForWidgets(
			context.bounds.left,
			context.bounds.top,
			context.bounds.width,
			context.bounds.height,
			unselectedWidgets,
			context.allSelectedWidgetsFreeMovement ? context.freeformSearchStep : null,
		);

		if (!found) {
			setAlertMessage(t.noSpaceForDuplication || 'No valid space available for the selected widgets.');
			setAlertType('warning');
			setShowAlert(true);
			return false;
		}

		const deltaX = left - context.bounds.left;
		const deltaY = top - context.bounds.top;
		const duplicateStamp = Date.now();
		const duplicatedIds = [];
		const duplicatedWidgets = context.selectedRects.map(({ widget, rect }, index) => {
			const duplicatedWidget = storeWidgetPosition(
				{
					...cloneDashboardValue(widget),
					id: `${widget.type}${duplicateStamp}-${index}`,
				},
				{
					left: rect.left + deltaX,
					top: rect.top + deltaY,
					width: rect.width,
					height: rect.height,
				},
				context.metrics,
			);
			duplicatedIds.push(duplicatedWidget.id);
			return duplicatedWidget;
		});

		setDashboard({
			...dashboard,
			widgets: sortWidgets([...currentWidgets, ...duplicatedWidgets]),
		});
		setSelectedWidgetIds(duplicatedIds);
		return true;
	};

	const removeSelectedGroup = () => {
		if (mode !== 'edit' || !multiSelectEnabled || selectedWidgetIds.length === 0) {
			return false;
		}

		const selectedIdSet = new Set(selectedWidgetIds.map((id) => String(id)));
		const nextWidgets = (dashboard?.widgets || []).filter((widget) => !selectedIdSet.has(String(widget.id)));

		setDashboard({
			...dashboard,
			widgets: sortWidgets(nextWidgets),
		});
		setSelectedWidgetIds([]);
		return true;
	};

	const toggleSelectedGroupFreeMovement = () => {
		if (mode !== 'edit' || !multiSelectEnabled || selectedWidgetIds.length === 0) {
			return false;
		}

		const selectedIdSet = new Set(selectedWidgetIds.map((id) => String(id)));
		const selectedWidgets = (dashboard?.widgets || []).filter((widget) => selectedIdSet.has(String(widget.id)));
		if (selectedWidgets.length === 0) return false;

		const shouldEnableFreeMovement = !selectedWidgets.every((widget) => isWidgetFreeMovementEnabled(widget));
		const updatedWidgets = (dashboard?.widgets || []).map((widget) => {
			if (!selectedIdSet.has(String(widget.id))) {
				return widget;
			}

			return {
				...widget,
				extra_props: {
					...(widget.extra_props || {}),
					freeMovement: shouldEnableFreeMovement,
				},
			};
		});

		setDashboard({
			...dashboard,
			widgets: sortWidgets(updatedWidgets),
		});
		return true;
	};

	const getSelectedGroupFreeMovementState = () => {
		const selectedIdSet = new Set(selectedWidgetIds.map((id) => String(id)));
		const selectedWidgets = (dashboard?.widgets || []).filter((widget) => selectedIdSet.has(String(widget.id)));
		if (selectedWidgets.length === 0) return false;
		return selectedWidgets.every((widget) => isWidgetFreeMovementEnabled(widget));
	};

	const startSelectedGroupDrag = (event) => {
		if (mode !== 'edit' || !multiSelectEnabled || selectedWidgetIds.length === 0 || event.button !== 0) {
			return false;
		}

		const context = getSelectedGroupMoveContext();
		if (!context) return false;

		selectedGroupDragRef.current = {
			startX: event.clientX,
			startY: event.clientY,
			context,
		};
		selectedGroupDragOffsetRef.current = { x: 0, y: 0 };
		suppressSelectionClickRef.current = true;
		setSelectedGroupDragOffset((currentOffset) => (
			sameOffset(currentOffset, { x: 0, y: 0 }) ? currentOffset : { x: 0, y: 0 }
		));

		const handlePointerMove = (moveEvent) => {
			const dragState = selectedGroupDragRef.current;
			if (!dragState) return;

			const rawDeltaX = moveEvent.clientX - dragState.startX;
			const rawDeltaY = moveEvent.clientY - dragState.startY;
			const nextOffset = clampSelectedGroupDelta(rawDeltaX, rawDeltaY, dragState.context);
			selectedGroupDragOffsetRef.current = nextOffset;
			setSelectedGroupDragOffset((currentOffset) => (
				sameOffset(currentOffset, nextOffset) ? currentOffset : nextOffset
			));
		};

		const handlePointerUp = () => {
			const dragState = selectedGroupDragRef.current;
			const finalOffset = selectedGroupDragOffsetRef.current;
			selectedGroupDragRef.current = null;
			selectedGroupDragOffsetRef.current = null;
			setSelectedGroupDragOffset((offset) => (offset === null ? offset : null));
			document.removeEventListener('pointermove', handlePointerMove);
			document.removeEventListener('pointerup', handlePointerUp);
			if (!dragState) return;
			if (finalOffset) {
				commitSelectedGroupMove(finalOffset.x, finalOffset.y);
			}
		};

		document.addEventListener('pointermove', handlePointerMove);
		document.addEventListener('pointerup', handlePointerUp);
		event.preventDefault();
		event.stopPropagation();
		return true;
	};

	const handleEditablePointerDown = (event) => {
		if (mode !== 'edit' || !multiSelectEnabled || event.button !== 0) return;
		const widgetElement = event.target?.closest?.('.widget');
		if (widgetElement) {
			if (
				selectedWidgetIdSet.has(String(widgetElement.id)) &&
				!event.target?.closest?.('.widget-buttons-container, .widget-resizer, .second-widget-resizer')
			) {
				startSelectedGroupDrag(event);
			}
			return;
		}

		const metrics = getEditableMetrics();
		const divertedElement = document.getElementById('diverted');
		if (!metrics || !divertedElement) return;

		const startX = event.clientX - metrics.divertedRect.left + metrics.scrollLeft;
		const startY = event.clientY - metrics.divertedRect.top + metrics.scrollTop;
		selectionDragRef.current = { startX, startY };
		setSelectionRect((currentRect) => {
			const nextRect = { left: startX, top: startY, width: 0, height: 0 };
			return sameRect(currentRect, nextRect) ? currentRect : nextRect;
		});
		setSelectedWidgetIds((currentIds) => (currentIds.length === 0 ? currentIds : []));

		const handlePointerMove = (moveEvent) => {
			if (!selectionDragRef.current) return;

			const currentMetrics = getEditableMetrics();
			if (!currentMetrics) return;

			const currentX = moveEvent.clientX - currentMetrics.divertedRect.left + currentMetrics.scrollLeft;
			const currentY = moveEvent.clientY - currentMetrics.divertedRect.top + currentMetrics.scrollTop;
			const nextRect = normalizeRect({
				left: selectionDragRef.current.startX,
				top: selectionDragRef.current.startY,
				width: currentX - selectionDragRef.current.startX,
				height: currentY - selectionDragRef.current.startY,
			});
			setSelectionRect((currentRect) => (sameRect(currentRect, nextRect) ? currentRect : nextRect));

			const nextSelectedIds = (dashboard?.widgets || [])
				.filter((widget) => rectsIntersect(getWidgetRectPx(widget, currentMetrics), nextRect))
				.map((widget) => widget.id);
			setSelectedWidgetIds((currentIds) => (
				sameIdList(currentIds, nextSelectedIds) ? currentIds : nextSelectedIds
			));
		};

		const handlePointerUp = () => {
			selectionDragRef.current = null;
			setSelectionRect(null);
			document.removeEventListener('pointermove', handlePointerMove);
			document.removeEventListener('pointerup', handlePointerUp);
		};

		document.addEventListener('pointermove', handlePointerMove);
		document.addEventListener('pointerup', handlePointerUp);
		event.preventDefault();
	};

	const handleEditableClickCapture = (event) => {
		if (mode !== 'edit' || !multiSelectEnabled) return;
		if (suppressSelectionClickRef.current) {
			suppressSelectionClickRef.current = false;
			event.preventDefault();
			event.stopPropagation();
			return;
		}
		const widgetElement = event.target?.closest?.('.widget');
		if (!widgetElement || !document.getElementById('editable')?.contains(widgetElement)) return;
		if (event.target?.closest?.('.widget-buttons-container, .widget-resizer, .second-widget-resizer')) return;

		const widgetId = widgetElement.id;
		if (!widgetId) return;

		setSelectedWidgetIds((currentIds) => {
			const alreadySelected = currentIds.some((id) => String(id) === String(widgetId));
			if (event.shiftKey || event.ctrlKey || event.metaKey) {
				return alreadySelected
					? currentIds.filter((id) => String(id) !== String(widgetId))
					: [...currentIds, widgetId];
			}
			return alreadySelected && currentIds.length === 1 ? currentIds : [widgetId];
		});
	};

	return {
		commitSelectedGroupMove,
		duplicateSelectedGroup,
		handleEditableClickCapture,
		handleEditablePointerDown,
		removeSelectedGroup,
		startSelectedGroupDrag,
		toggleSelectedGroupFreeMovement,
		getSelectedGroupFreeMovementState,
	};
};
